import { parseDocument, uploadPrinceFile } from '@/api/queries/prince';
import { buildXmlAttributes } from '@/features/prince/attachment-utils';
import { type ParsedItem } from '@/features/types/attachments';
import { useConversationId } from '@/hooks/useConversationId';
import { Paperclip } from 'lucide-react';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ChatInputRef } from '../../components/chat/ChatInput';
import { ChatInput } from '../../components/chat/ChatInput';

interface PrinceChatInputProps {
  isStreaming: boolean;
  onAbortStreaming: () => void;
  onSend: (message: string) => void;
  onAttachment: () => void;
  disabled: boolean;
}

export type PrinceChatInputRef = {
  clear: () => void;
};

export const PrinceChatInput = forwardRef<PrinceChatInputRef, PrinceChatInputProps>(
  function PrinceChatInputWithRef(
    { onSend, onAttachment, disabled = false, isStreaming, onAbortStreaming }: PrinceChatInputProps,
    ref
  ) {
    const { t } = useTranslation();
    const { currentConversationId } = useConversationId();
    const { t: tPrince } = useTranslation('prince');
    const chatInputRef = useRef<ChatInputRef>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // Optional external hook on attachment click
    const notifyAttachmentClick = () => {
      try {
        onAttachment();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : tPrince('chatInput.attachmentError'));
      }
    };

    const [activeFiles, setActiveFiles] = useState<{ [key: string]: ParsedItem }>({});

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          chatInputRef.current?.clear();
          setActiveFiles({});
        },
      }),
      []
    );

    const imageExtensions = useMemo(
      () => new Set(['png', 'jpeg', 'jpg', 'tiff', 'bmp', 'webp']),
      []
    );

    const triggerFileDialog = () => {
      notifyAttachmentClick();
      if (!fileInputRef.current) return;
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    };

    const isImageFile = useCallback(
      (filename: string, documentType?: string | null): boolean => {
        const isDocumentTypeImage =
          !!documentType && imageExtensions.has(documentType.toLowerCase());
        return imageExtensions.has(filename.split('.').pop() || '') || isDocumentTypeImage;
      },
      [imageExtensions]
    );

    const setFilePending = useCallback((file: File) => {
      const item = {
        filename: file.name,
        contentMarkdown: '',
        documentType: null,
        tempUrl: undefined,
        metadata: undefined,
        loading: true,
      };
      setActiveFiles(prev => ({ ...prev, [item.filename]: item }));
    }, []);

    const processFiles = async (files: File[]) => {
      const currentCount = Object.keys(activeFiles).length;
      const availableSlots = Math.max(0, 5 - currentCount);
      if (files.length > availableSlots) {
        toast.error(tPrince('chatInput.maxAttachmentsReached', { count: availableSlots }));
      }

      const toProcess = files.slice(0, availableSlots);
      const failedFiles: string[] = [];

      for (const file of toProcess) {
        try {
          setFilePending(file);

          type DocumentParseResponse = {
            filename?: string;
            contentMarkdown?: string;
            documentType?: string | null;
            pageCount?: number;
            metadata?: {
              title?: string;
              subject?: string;
              author?: string;
              creationDate?: string;
              modificationDate?: string;
              fileSizeBytes?: number;
            };
          };

          // Perform BOTH document parsing AND temporary upload in parallel
          const [parseResult, tempUploadResult] = await Promise.allSettled([
            parseDocument<DocumentParseResponse>(file),
            uploadPrinceFile(file, currentConversationId!),
          ]);

          // Process document parsing result
          let filename: string = file.name;
          let contentMarkdown: string = '';
          let documentType: string | null | undefined = undefined;
          let pageCount: number | undefined = undefined;
          let metadata = undefined;

          if (parseResult.status === 'fulfilled') {
            const result = parseResult.value;
            filename = typeof result === 'string' ? file.name : result.filename || file.name;
            contentMarkdown = typeof result === 'string' ? result : result.contentMarkdown || '';
            documentType = typeof result === 'string' ? undefined : result.documentType;
            pageCount = typeof result === 'string' ? undefined : result.pageCount;
            metadata = typeof result === 'string' ? undefined : result.metadata;
          } else {
            throw parseResult.reason;
          }

          // Process temp upload result
          let tempUrl: string | undefined = undefined;
          if (tempUploadResult.status === 'fulfilled') {
            tempUrl = tempUploadResult.value.url;
          } else {
            console.error('Temporary upload failed for', file.name, ':', tempUploadResult.reason);
          }

          const item: ParsedItem = {
            filename,
            contentMarkdown,
            documentType,
            tempUrl,
            metadata: metadata
              ? {
                  ...metadata,
                  pageCount,
                }
              : { pageCount },
            loading: false,
          };

          setActiveFiles(prev => ({ ...prev, [item.filename]: item }));
        } catch (err) {
          console.error('Document parse failed:', err);
          failedFiles.push(file.name);
          setActiveFiles(prev => {
            const newFiles = { ...prev };
            delete newFiles[file.name];
            return newFiles;
          });
        }
      }

      if (failedFiles.length > 0) {
        toast.error(tPrince('chatInput.parseSomeFailed', { count: failedFiles.length }));
      }
    };

    const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;
      await processFiles(Array.from(files));
    };

    const handlePaste = async (files: File[]) => {
      if (files.length === 0) return;  
      await processFiles(files);
    };

    const handleSend = (message: string) => {
      const messageToSend = message.trim();
      const allFiles = Object.values(activeFiles);

      // Only send if there's text or attachments
      if (!messageToSend && allFiles.length === 0) return;

      let payloadContent = messageToSend;

      const documents = allFiles.filter(file => !isImageFile(file.filename, file.documentType));
      const images = allFiles.filter(file => isImageFile(file.filename, file.documentType));

      // Inject XML blocks into the payload (sent to backend) but NOT displayed in UI
      if (documents.length > 0) {
        const documentContent = documents
          .map(d => `<document ${buildXmlAttributes(d)}>\n${d.contentMarkdown}\n</document>`)
          .join('\n\n');
        payloadContent = `${payloadContent}\n\n${documentContent}`;
      }

      if (images.length > 0) {
        const imageContent = images
          .map(img => `<image ${buildXmlAttributes(img)}>\n${img.contentMarkdown}\n</image>`)
          .join('\n\n');
        payloadContent = `${payloadContent}\n\n${imageContent}`;
      }

      onSend(payloadContent);
      setActiveFiles({});
      chatInputRef.current?.clear();
    };

    const attachments = Object.values(activeFiles).map(file => ({
      filename: file.filename,
      removeLabel: isImageFile(file.filename, file.documentType)
        ? tPrince('chatInput.removeImage')
        : tPrince('chatInput.removeDocument'),
      loading: file.loading,
      onRemove: () =>
        setActiveFiles(prev => {
          const newFiles = { ...prev };
          delete newFiles[file.filename];
          return newFiles;
        }),
    }));

    return (
      <>
        <ChatInput
          ref={chatInputRef}
          placeholder={tPrince('chatInput.placeholder')}
          onSend={handleSend}
          isSending={isStreaming}
          onAbortSending={onAbortStreaming}
          onPaste={handlePaste}
          attachments={attachments}
          actionButtons={[
            {
              key: 'attachment',
              icon: <Paperclip className="h-3.5 w-3.5" />,
              label: t('chats.addAttachment'),
              onClick: triggerFileDialog,
            },
          ]}
          disabled={disabled}
          disableSend={disabled || Object.values(activeFiles).some(file => file.loading)}
        />
        {/* Hidden file input for attachments */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelected}
          className="hidden"
          data-testid="hidden-file-input"
        />
      </>
    );
  }
);
